// components/ExperiencesList.jsx
import React, { useEffect, useState } from 'react';
import { Star, Link as LinkIcon, ChevronDown, ChevronUp, Route as RouteIcon, Bike } from 'lucide-react';
import config from '../config';
import { useTranslation } from 'react-i18next';

const ExperiencesList = ({
  searchQuery = '',
  setActiveSidebarTab,
  setMapListView,
  setIsSidebarOpen
}) => {
  const [experiences, setExperiences] = useState([]);
  const [expandedExperienceId, setExpandedExperienceId] = useState(null);
  const preferredLanguage = localStorage.getItem('preferredLanguage') || 'en';
  const { t } = useTranslation();
  const [user, setUser] = useState(null);
  const token = localStorage.getItem('token');

  useEffect(() => {
    const fetchExperiences = async () => {
      try {
        const res = await fetch(config.getApiUrl('experiences'));
        const data = await res.json();
        if (data.success) {
          setExperiences(data.experiences);
        }
      } catch (err) {
        console.error('Error fetching experiences:', err);
      }
    };
    
    fetchExperiences();
  }, []);

    useEffect(() => {
        const fetchUserProfile = async () => {
        if (token) {
            try {
            const res = await fetch(config.getApiUrl('users/me'), {
                headers: {
                Authorization: `Bearer ${token}`
                }
            });
            const data = await res.json();

            if (res.ok && data.user) {
                setUser(data.user);
            } else {
                console.error('Error loading user profile');
            }
            } catch (error) {
            console.error('Error fetching user profile:', error);
            }
        }
        };

        fetchUserProfile();
    }, [token]);

  const filteredExperiences = experiences.filter((exp) => {
    const name = exp.name?.[preferredLanguage] || exp.name?.en || '';
    const description = exp.description?.[preferredLanguage] || exp.description?.en || '';
    return (
      name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      description.toLowerCase().includes(searchQuery.toLowerCase())
    );
  });

  if (!user) {
    return (
      <div className="p-6 text-center text-gray-600">
          {t('experience.loginToView', 'Please log in to view available experiences.')}

          <div>
              <button
              onClick={() => {
                  setActiveSidebarTab('profile');
                  setMapListView('list');
                  setIsSidebarOpen(true);
              }}
              className="mt-2 text-blue-600 underline text-sm"
              >
              {t('profile.goToProfile', 'Go to Profile')}
              </button>
          </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {filteredExperiences.map((exp) => {
        const name = exp.name?.[preferredLanguage] || exp.name?.en || 'Unnamed';
        const description = exp.description?.[preferredLanguage] || exp.description?.en || '';
        const isExpanded = expandedExperienceId === exp._id;

        return (
          <div key={exp._id} className="border border-gray-200 rounded-lg p-4 shadow-sm hover:shadow-md transition relative">
            <div
              className="flex items-center justify-between mb-2 cursor-pointer"
              onClick={() => setExpandedExperienceId(isExpanded ? null : exp._id)}
            >
              <div className="flex items-center space-x-2">
                {exp.image ? (
                  <img src={exp.image} alt={name} className="h-10 w-10 rounded-full object-cover border border-gray-300" />
                ) : (
                  <Star className="h-5 w-5 text-yellow-500" />
                )}
                <h3 className="text-lg font-semibold text-gray-800">{name}</h3>
              </div>
              {isExpanded ? <ChevronUp className="h-5 w-5 text-gray-500" /> : <ChevronDown className="h-5 w-5 text-gray-500" />}
            </div>

            <p className="text-sm text-gray-600 mb-2">{description}</p>

            {isExpanded && (
            <div className="mt-4 space-y-3">
                {exp.image && (
                <img
                    src={exp.image}
                    alt={name}
                    className="w-full h-40 object-cover rounded-lg shadow-sm"
                />
                )}

                <div className="space-y-2">
                <button
                    className="w-full flex items-center px-4 py-2 border border-blue-200 rounded-lg text-blue-700 hover:bg-blue-50 transition"
                    onClick={() => setActiveSidebarTab('bikerental')}
                >
                    <Bike className="w-4 h-4 mr-2" />
                    {t('experience.rentBike', 'Rent a Bike')}
                </button>

                {exp.routeId && (
                    <button
                    className="w-full flex items-center px-4 py-2 border border-purple-200 rounded-lg text-purple-700 hover:bg-purple-50 transition"
                    onClick={() => {
                        window.location.hash = `#route-${exp.routeId}`;
                        setActiveSidebarTab('routes');
                        setMapListView('list');
                        setIsSidebarOpen(true);
                    }}
                    >
                    <RouteIcon className="w-4 h-4 mr-2" />
                    {t('experience.viewRoute', 'View Route')}
                    </button>
                )}

                <a
                    href={exp.customLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-full flex items-center px-4 py-2 border border-green-200 rounded-lg text-green-700 hover:bg-green-50 transition"
                >
                    <LinkIcon className="w-4 h-4 mr-2" />
                    {t('experience.moreInfo', 'More Info')}
                </a>
                </div>
            </div>
            )}

          </div>
        );
      })}
    </div>
  );
};

export default ExperiencesList;
